import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Modal from '../../components/Modal';
import SearchSelect from '../../components/SearchSelect';
import { useDictText } from '../../components/DictSelect';
import {
    CALIB_IMPORT_TEMPLATE_PATH,
    CalibImportPreview,
    deviceLabel,
    importDeviceCalibration,
} from '../../api/device';
import { alertApprovalIfPending, ApiError, downloadFile } from '../../api/request';
import { actions, useDeviceCtx } from '../../store';
import { alertDialog } from '../../utils/dialog';

interface Props {
    open: boolean;
    onClose: () => void;
    /** 直接入库成功后回调 (走审批流时数据未变, 不触发) */
    onImported?: (deviceKey: string) => void;
}

// 功率标定曲线单独导入 (xlsx / yaml, 2026-09-15):
//   选目标设备 → 下载模板/选文件 → 自动 dryRun 预检 (解析 + 校验 + 当前点数) → 确认导入.
// 文件两种: xlsx 按模板两列; 设备 YAML (.yaml/.yml) 只取顶层 power_calibration 段, 整份或精简都行.
// 语义 = 按 device_key 整曲线覆盖: 目标设备已有校准点时明确提示"将全部覆盖", 且按钮文案改为"覆盖导入".
// 目标设备缺省取页面当前设备, 也可在弹窗里改选 (导入成功后页面切到该设备).
const CalibrationImportModal = ({ open, onClose, onImported }: Props) => {
    const ctx = useDeviceCtx();
    const dispatch = useDispatch();
    const lightText = useDictText('light_source');
    const [deviceKey, setDeviceKey] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<CalibImportPreview | null>(null);
    const [checking, setChecking] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [tplBusy, setTplBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // 打开时目标设备跟随页面当前设备; 关闭清空
    useEffect(() => {
        if (open) {
            setDeviceKey(ctx.curDeviceKey || '');
        } else {
            setFile(null);
            setPreview(null);
            setError('');
            setBusy(false);
            setDragOver(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 设备 + 文件都齐了就自动预检; 任一变化重新预检 (覆盖提示依赖目标设备的当前点数)
    useEffect(() => {
        if (!open || !file || !deviceKey) {
            setPreview(null);
            return;
        }
        let stale = false;
        setChecking(true);
        setError('');
        importDeviceCalibration(file, deviceKey, true)
            .then(({ data }) => {
                if (!stale) setPreview(data);
            })
            .catch((e) => {
                if (stale) return;
                setPreview(null);
                setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
            })
            .finally(() => {
                if (!stale) setChecking(false);
            });
        return () => {
            stale = true;
        };
    }, [open, file, deviceKey]);

    const pickFile = (f: File | null | undefined) => {
        if (!f) return;
        if (!/\.(xlsx|ya?ml)$/i.test(f.name)) {
            setError('仅支持 .xlsx / .yaml / .yml 文件');
            return;
        }
        setFile(f);
        setError('');
    };

    // 两种模板: xlsx (按列填) / yaml (power_calibration 段), 同一接口按 ?format 分流
    const onDownloadTemplate = async (format: 'xlsx' | 'yaml') => {
        setTplBusy(true);
        try {
            await downloadFile(
                format === 'yaml' ? `${CALIB_IMPORT_TEMPLATE_PATH}?format=yaml` : CALIB_IMPORT_TEMPLATE_PATH,
                `calibration-import-template.${format}`,
            );
        } catch (e) {
            setError(`模板下载失败: ${e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)}`);
        } finally {
            setTplBusy(false);
        }
    };

    const onSubmit = async () => {
        if (!file || !deviceKey || !preview) return;
        setBusy(true);
        setError('');
        try {
            const { data, message } = await importDeviceCalibration(file, deviceKey);
            if (alertApprovalIfPending(message)) {
                onClose();
                return;
            }
            const warns = data?.warnings?.length
                ? `\n⚠ 软告警:\n${data.warnings.map((w) => `· ${w}`).join('\n')}`
                : '';
            alertDialog(`${message || '导入成功'}${warns}`);
            dispatch(actions.device.setCur(deviceKey));
            if (onImported) onImported(deviceKey);
            onClose();
        } catch (e) {
            setError(`导入失败: ${e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)}`);
        } finally {
            setBusy(false);
        }
    };

    const cur = ctx.devices.find((d) => d.deviceKey === deviceKey);
    const canSubmit = !!file && !!deviceKey && !!preview && !checking && !busy;

    return (
        <Modal open={open} onClose={onClose} title="导入功率校准点 (xlsx / yaml)" width={720}>
            <div className="space-y-4">
                <div className="text-xs text-slate-500">
                    先下载模板:
                    <a
                        className="text-brand-600 hover:underline mx-1 cursor-pointer"
                        onClick={() => onDownloadTemplate('xlsx')}
                    >
                        xlsx 模板
                    </a>
                    (两列: 软件功率 % / 真实功率 W) 或
                    <a
                        className="text-brand-600 hover:underline mx-1 cursor-pointer"
                        onClick={() => onDownloadTemplate('yaml')}
                    >
                        yaml 模板
                    </a>
                    (<code className="font-mono">power_calibration</code> 段, 与设备工况 YAML 同格式, 整份设备
                    YAML 也可直接上传){tplBusy && <span className="text-slate-400 ml-1">下载中…</span>}.
                    至少 2 个点; 选择目标设备后上传, 普通用户走审批, 管理员通过后生效.
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-semibold">目标设备</span>
                    <SearchSelect
                        className="w-96"
                        value={deviceKey}
                        onChange={setDeviceKey}
                        disabled={!ctx.loaded || ctx.devices.length === 0}
                        placeholder={!ctx.loaded ? '加载中…' : '搜 deviceKey / 设备 / 架构'}
                        options={ctx.devices.map((d) => ({
                            value: d.deviceKey,
                            label: `${d.archCode} · ${deviceLabel(d, lightText)} · ${d.deviceKey}`,
                        }))}
                    />
                    {cur && (
                        <span className="tag bg-slate-100 text-slate-600 font-mono" title="deviceKey (业务主键)">
                            {cur.deviceKey}
                        </span>
                    )}
                </div>

                {/* 覆盖提示: 目标设备已有校准点时醒目提示 (按 device_key 整曲线覆盖) */}
                {preview && preview.overwrite && (
                    <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
                        ⚠ 设备 <code className="font-mono">{preview.deviceKey}</code> 已有{' '}
                        <b>{preview.existingCount}</b> 个校准点, 本次导入会按 device_key{' '}
                        <b>整体覆盖</b>为文件中的 {preview.count} 个点 (先清空再写入, 不做逐点合并).
                    </div>
                )}
                {preview && !preview.overwrite && (
                    <div className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded px-3 py-2">
                        ℹ 设备 <code className="font-mono">{preview.deviceKey}</code> 当前没有校准点, 本次导入将新建{' '}
                        {preview.count} 个点.
                    </div>
                )}

                <div
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        pickFile(e.dataTransfer.files?.[0]);
                    }}
                    className={`border-2 border-dashed rounded-lg p-6 text-center text-sm cursor-pointer ${
                        dragOver
                            ? 'border-brand-400 bg-brand-50/40'
                            : 'border-slate-200 hover:border-slate-300'
                    }`}
                >
                    {file ? (
                        <div className="text-slate-700 font-mono text-xs">
                            {file.name}{' '}
                            <span className="text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                            <div className="text-[11px] text-brand-600 font-sans mt-1">点击或拖拽其它文件替换</div>
                        </div>
                    ) : (
                        <span className="text-slate-400">点击选择 或 拖拽 xlsx / yaml 文件到此处</span>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".xlsx,.yaml,.yml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/yaml,application/x-yaml"
                        className="hidden"
                        onChange={(e) => {
                            pickFile(e.target.files?.[0]);
                            e.target.value = '';
                        }}
                    />
                </div>

                {checking && <div className="text-xs text-slate-400">预检中…</div>}

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50 whitespace-pre-wrap">
                        {error}
                    </div>
                )}

                {preview && !checking && (
                    <div className="space-y-3">
                        {preview.warnings.length > 0 && (
                            <div className="card p-3 text-xs text-amber-800 border-amber-200 bg-amber-50">
                                <div className="font-semibold mb-1">软告警 (不拦截, 按原值导入):</div>
                                {preview.warnings.map((w) => (
                                    <div key={w}>· {w}</div>
                                ))}
                            </div>
                        )}
                        <div className="card overflow-x-auto">
                            <div className="px-3 py-2 bg-slate-50 text-sm">
                                解析预览 · 共 {preview.count} 点
                            </div>
                            <div className="max-h-56 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-t border-slate-100">
                                        <tr>
                                            <th className="th">软件功率 %</th>
                                            <th className="th">真实功率 W</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.points.map((p) => (
                                            <tr key={p.pct}>
                                                <td className="td font-mono text-xs">{p.pct}</td>
                                                <td className="td font-mono text-xs">{p.realW}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" onClick={onClose} className="btn-ghost" disabled={busy}>
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onSubmit}
                        disabled={!canSubmit}
                    >
                        {busy ? '导入中…' : preview?.overwrite ? `覆盖导入 (替换原 ${preview.existingCount} 点)` : '导入'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CalibrationImportModal;
